// Refinery Macros — record & replay, tailored to the File Refinery's async flow
// (Jul 2026, Randy). Unlike the Workbench macro player (synchronous, 3-slot,
// batch-over-a-YouTube-library), a Refinery macro records a single big-file
// workflow: optionally pull a YouTube transcript, pick an operation + output
// size, choose where the result lands (Staging / Tier 1/2/3), and Run. The Run
// step kicks off a BACKGROUND job and the player WAITS for it to finish (polling
// the same job the progress card shows) before moving on. If the file is too big
// for one job, or chunks get skipped/lost, the user is told in a clear alert —
// the same idea as the Workbench macro's "too large" notice.
import { useEffect, useRef, useState } from "react";
import {
  Play, Pause, Stop, Circle, Trash, PencilSimple, CaretDown, CaretRight,
  FloppyDisk, X, Sparkle, Record as RecordIcon, ArrowUp, ArrowDown, Check, Warning,
  Lightning, Bell, ArrowClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import {
  HEAVY_BOOST_MODELS, SMALL_BOOST_MODELS, boostModelLabel, boostModelMeta,
  isHeavyBoostModel, boostCostEstimateCents,
} from "@/components/BoostButton";
import { toast } from "sonner";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const clampDelay = (ms) => Math.min(8000, Math.max(300, ms || 600));

const DEST_LABEL = (tier) => (tier === 0 || tier == null ? "Stage only" : `Commit to T${tier}`);

// Jul 2026 (Randy) — batch skip-vs-halt classification, mirrored from the
// Workbench. A per-video skip leaves the video STRUCK + listed in the skip
// report and the batch keeps going; a HALT code stops the whole batch because
// retrying the next video won't help (systemic problem).
const REASON_LABEL = {
  NO_TRANSCRIPT: "No transcript exists",
  BLANK: "Blank transcript",
  UNAVAILABLE: "Video unavailable / private",
  FETCH_FAILED: "Transcript fetch failed",
  QUOTA: "YouTube quota exceeded",
  KEY: "YouTube API key problem",
  VOUCHER: "Silo voucher empty",
  REFINERY_TOOBIG: "Too big for one job — needs Boost / manual run",
  RUN_FAILED: "The operation failed",
  EMPTY_OUTPUT: "No output was produced",
  UNKNOWN: "Skipped (unexpected error)",
};
const HALT_CODES = new Set(["QUOTA", "KEY", "VOUCHER"]);

export default function RefineryMacros({
  busRef, ytApiRef, macroApiRef, mode, setMode, siloId,
  recordingRef, recordBufRef, lastStepAtRef, recordCount, setRecordCount,
}) {
  const [macros, setMacros] = useState([]);
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const macroLocked = cpLocked("macro_autobuild");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [editStep, setEditStep] = useState(null);
  const [gapAlert, setGapAlert] = useState(null); // { macroName, reason, kind }
  const [doneModal, setDoneModal] = useState(null); // { title, message }
  const [heavyConfirm, setHeavyConfirm] = useState(null); // { macro, estCents, model, videos } — premium-model cost gate
  // Per-macro batch settings (mirror of the Workbench). Seeded from the saved
  // macro so leaving the page / Clear Refinery never resets them.
  const [countByMacro, setCountByMacro] = useState({});
  const [tierByMacro, setTierByMacro] = useState({});
  const [skipPausesByMacro, setSkipPausesByMacro] = useState({});
  const [notifyByMacro, setNotifyByMacro] = useState({});
  const [savingSettingsId, setSavingSettingsId] = useState(null);
  const [skipReport, setSkipReport] = useState([]); // [{id,title,reason,code,libId,video_id}] — persisted per silo

  const [play, setPlay] = useState({ name: "", idx: 0, total: 0, stepLabel: "", editing: false });
  const pausedRef = useRef(false);
  const abortRef = useRef(false);

  const load = async () => {
    try {
      const r = await api.get("/workbench/macros?surface=file_refinery");
      const list = r.data || [];
      setMacros(list);
      // Seed the per-macro batch settings from what was SAVED.
      const c = {}, t = {}, sp = {}, nf = {};
      for (const m of list) {
        c[m.id] = m.run_count || 1;
        t[m.id] = m.tier || 2;
        sp[m.id] = m.skip_pauses !== false; // default ON
        nf[m.id] = !!m.notify_on_finish;
      }
      setCountByMacro(c);
      setTierByMacro(t);
      setSkipPausesByMacro(sp);
      setNotifyByMacro(nf);
    } catch { /* section just shows empty */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Feb 17, 2026 (Randy pt-macro-selected + pt-run-selected-macro-fix) —
  // mirror of WorkbenchMacros. The selected macro is the one the YouTube
  // Tool's "Run selected Macro" button uses. Backend stamps `is_selected`
  // on exactly one row; fallback (when the user hasn't picked yet) =
  // earliest-created macro (computed on read, not persisted).
  const selectedMacro = macros.find((m) => m.is_selected) || macros[0] || null;
  const selectedMacroId = selectedMacro?.id || null;

  const selectMacro = async (macro) => {
    if (!macro || macro.id === selectedMacroId) return;
    if (selectedMacroId) {
      const ok = window.confirm(
        `Change currently selected Macro to be your default?\n\nCurrent: ${selectedMacro?.name || "(none)"}\nNew: ${macro.name}`
      );
      if (!ok) return;
    }
    try {
      await api.post(`/workbench/macros/${macro.id}/select?surface=file_refinery`);
      setMacros((ms) => ms.map((m) => ({ ...m, is_selected: m.id === macro.id })));
      toast.success(`"${macro.name}" is now your default Refinery macro.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update selection.");
    }
  };

  const runMacroRef = useRef(null);
  useEffect(() => {
    if (!macroApiRef) return;
    macroApiRef.current = {
      getSelectedMacro: () => selectedMacro,
      runSelectedMacroOnUrl: async (urlOverride) => {
        if (!selectedMacro) { toast.error("No macro selected."); return; }
        // Feb 17, 2026 (Randy pt-run-selected-macro-clear-staging-only). Just
        // clear STAGING — DO NOT bump child-clear (that clears the URL /
        // output / pull, pulling the rug out from under the macro). Matches
        // how batch mode already clears between videos.
        try { busRef.current?.clearStaging?.(); } catch { /* noop */ }
        await runMacroRef.current?.(selectedMacro, { singleUrl: urlOverride });
      },
    };
  });

  // ---------- Skip report (per-silo, persisted) ----------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`mysilo_refinery_macro_skips_${siloId || "none"}`);
      setSkipReport(raw ? JSON.parse(raw) : []);
    } catch { setSkipReport([]); }
  }, [siloId]);
  const persistSkips = (list) => {
    setSkipReport(list);
    try { localStorage.setItem(`mysilo_refinery_macro_skips_${siloId || "none"}`, JSON.stringify(list)); } catch { /* ignore */ }
  };
  const reattemptSkip = (item) => {
    try { if (item.libId && item.video_id) (ytApiRef.current || {}).unStrikeVideo?.(item.libId, item.video_id); } catch { /* ignore */ }
    persistSkips(skipReport.filter((s) => s.id !== item.id));
    toast.success(`“${(item.title || "video").slice(0, 40)}” un-struck — it'll be picked up next run.`);
  };
  const deleteSkip = (item) => { persistSkips(skipReport.filter((s) => s.id !== item.id)); };

  // Does this macro replay on a Boost, and is that a premium "heavy-hitter" model?
  const macroBoostStep = (macro) => (macro.steps || []).find((s) => s.type === "run" && s.params?.boost?.target_model);
  const macroIsHeavy = (macro) => {
    const b = macroBoostStep(macro);
    return b ? isHeavyBoostModel(b.params.boost.target_model) : false;
  };

  // ---------- Recording ----------
  const startRecording = () => {
    if (macroLocked) { cpOpenGate("macro_autobuild"); return; }
    if (mode !== "idle") return;
    recordBufRef.current = [];
    lastStepAtRef.current = 0;
    recordingRef.current = true;
    setRecordCount(0);
    setPendingSave(false);
    setMode("recording");
    toast.success("Recording… run your Refinery workflow once, then Stop.");
  };
  const stopRecording = () => {
    recordingRef.current = false;
    setMode("idle");
    const steps = recordBufRef.current || [];
    if (!steps.length) { toast.message("Nothing recorded — no actions were captured."); return; }
    setSaveName("");
    setPendingSave(true);
  };
  const discardRecording = () => {
    recordingRef.current = false;
    recordBufRef.current = [];
    setRecordCount(0);
    setPendingSave(false);
    setMode("idle");
    toast.message("Recording discarded.");
  };
  const saveMacro = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("Give your macro a name."); return; }
    const steps = recordBufRef.current || [];
    if (!steps.length) { setPendingSave(false); return; }
    setSaving(true);
    try {
      await api.post("/workbench/macros", { name, steps, surface: "file_refinery" });
      toast.success(`Macro "${name}" saved · ${steps.length} steps.`);
      recordBufRef.current = [];
      setRecordCount(0);
      setPendingSave(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save macro.");
    } finally { setSaving(false); }
  };

  // ---------- Playback ----------
  const waitWhilePaused = async () => { while (pausedRef.current && !abortRef.current) await sleep(200); };

  const executeStep = async (step, opts = {}) => {
    switch (step.type) {
      case "yt_fetch": {
        const yt = ytApiRef.current || {};
        if (yt.fetch) await yt.fetch(step.params?.kind || "transcript");
        break;
      }
      case "yt_transfer": {
        const yt = ytApiRef.current || {};
        if (yt.transfer) await yt.transfer(step.params?.target || "staging");
        break;
      }
      case "set_output_size":
        busRef.current?.setOutputSize?.(step.params?.pct ?? null);
        break;
      case "set_destination":
        // In a batch the panel's tier is authoritative (applied once up front),
        // so a recorded destination step is skipped to avoid fighting it.
        if (opts.batch) break;
        busRef.current?.setDestination?.(Number(step.params?.tier) || 0);
        break;
      case "run": {
        // Read primitives off busRef.current FRESH (it's reassigned every render)
        // so the just-selected operation / output / destination are current.
        if (step.params?.command_id) busRef.current?.selectOperation?.(step.params.command_id);
        await sleep(300);
        // Awaits the async background job to full completion, then returns its
        // terminal status so we can flag skipped/lost chunks afterwards.
        const res = await busRef.current?.runRefineryJob?.();
        return res || null;
      }
      case "pause_for_editing":
        if (opts.skipPauses) break; // batch "Skip pauses" toggle — glide past
        pausedRef.current = true;
        setMode("paused");
        setPlay((p) => ({ ...p, editing: true }));
        await waitWhilePaused();
        setPlay((p) => ({ ...p, editing: false }));
        break;
      default:
        break;
    }
    return null;
  };

  const runMacro = async (macro, opts = {}) => {
    if (macroLocked) { cpOpenGate("macro_autobuild"); return; }
    if (mode !== "idle") return;
    const steps = macro.steps || [];
    if (!steps.length) { toast.error("This macro has no steps."); return; }

    // Feb 17, 2026 (Randy pt-run-selected-macro-fix) — see WorkbenchMacros
    // for the full contract. `opts.singleUrl` forces wanted=1, skips the
    // library preflight, uses the provided URL as the authoritative target,
    // and calls `ensureVideoStruckByUrl` after the run completes.
    const singleUrl = typeof opts.singleUrl === "string" ? opts.singleUrl.trim() : "";
    const wanted = singleUrl ? 1 : Math.max(1, Math.min(50, countByMacro[macro.id] || 1));
    const tier = tierByMacro[macro.id] || 2;
    const batch = wanted > 1;
    const skipPauses = batch ? (skipPausesByMacro[macro.id] !== false) : false;
    const notify = batch && !!notifyByMacro[macro.id];
    const usesYouTube = steps.some((s) => s.type === "yt_fetch" || s.type === "yt_transfer");
    const yt = () => ytApiRef.current || {};

    // Heavy-hitter cost gate — fires BEFORE anything starts. Estimate scales with
    // the batch size. Whatever the creator picks after being warned is on them.
    const boostStep = macroBoostStep(macro);
    if (boostStep && isHeavyBoostModel(boostStep.params.boost.target_model) && !opts.heavyConfirmed) {
      const b = boostStep.params.boost;
      const inputChars = Math.max(25000, busRef.current?.getInputChars?.() || 0);
      const ratio = b.desired_output_chars && inputChars ? Math.min(1, Math.max(0.05, b.desired_output_chars / inputChars)) : 0.3;
      const estCents = boostCostEstimateCents(b.target_model, inputChars, ratio) * (batch ? wanted : 1);
      setHeavyConfirm({ macro, estCents, model: boostModelLabel(b.target_model), videos: batch ? wanted : 1 });
      return;
    }

    // Pre-flight: bail cleanly before entering "playing" if there's nothing to run on.
    // singleUrl runs skip this — the URL is the authority.
    if (usesYouTube && !singleUrl) {
      const first = yt().getNextUnstruckVideo?.();
      if (!first) {
        const st = yt().getState?.() || {};
        if (!st.url) { toast.error("No video to process: no un-struck videos in the library and no URL loaded. Fetch a channel's library (or paste a URL) first."); return; }
        if (batch) { toast.error("Batch needs a channel library with un-struck videos — fetch a channel first."); return; }
      }
    }
    if (singleUrl) {
      try { yt().loadVideo?.(singleUrl); } catch { /* noop */ }
    }

    pausedRef.current = false;
    abortRef.current = false;
    setMode("playing");
    setGapAlert(null);

    // Arm the macro's Boost (if it recorded one) as a persistent session for the
    // whole run/batch. The background Refinery job picks it up automatically.
    let boostArmed = false;
    if (boostStep) {
      try { await busRef.current?.armBoostForMacro?.(boostStep.params.boost); boostArmed = true; }
      catch { toast.error("Couldn't arm the macro's Boost — running on the default model."); }
    }
    // In a batch the panel's tier is authoritative — every job auto-commits to it.
    if (batch) { busRef.current?.setDestination?.(tier); await sleep(300); }

    const committed = [];
    const skippedNow = [];
    let processed = 0;
    let stopMsg = null;
    let lastTitle = "";
    let lastRun = null;
    // Every video already handled this batch (committed OR skipped). Passed to
    // getNextUnstruckVideo so the loop always advances UPWARD past them.
    const processedIds = new Set();
    const nextFreshVideo = () => yt().getNextUnstruckVideo?.(processedIds) || null;

    try {
      for (let v = 0; v < wanted; v++) {
        if (abortRef.current) break;
        let targetVideo = null;
        if (usesYouTube && !singleUrl) {
          targetVideo = nextFreshVideo();
          if (!targetVideo) {
            const st = yt().getState?.() || {};
            if (v === 0 && st.url && !batch) {
              // single manual-URL run — allowed
            } else {
              stopMsg = v === 0 ? "no un-struck videos to process" : "reached the top — no more un-struck videos";
              break;
            }
          }
        }
        if (targetVideo) {
          // Glide to the next un-struck video, load its URL and STRIKE it now so
          // you see it cross off. If it later fails we un-strike it below.
          yt().loadVideo?.(targetVideo.url);
          yt().strikeVideo?.(targetVideo.libId, targetVideo.video_id);
          processedIds.add(targetVideo.video_id);
          await sleep(600);
        }
        // Batch processes a fresh result per video; N=1 preserves any staging.
        if (batch) { busRef.current?.clearStaging?.(); await sleep(150); }
        lastTitle = targetVideo?.title || "";
        lastRun = null;
        setPlay({ name: macro.name, idx: 0, total: steps.length, stepLabel: "", editing: false, video: lastTitle, batchIdx: v + 1, batchTotal: wanted });
        if (targetVideo) toast.message(`${batch ? `(${v + 1}/${wanted}) ` : ""}Processing “${lastTitle.slice(0, 48)}”…`);

        try {
          for (let i = 0; i < steps.length; i++) {
            await waitWhilePaused();
            if (abortRef.current) break;
            setPlay((p) => ({ ...p, idx: i + 1, stepLabel: steps[i].label || steps[i].type }));
            await sleep(clampDelay(steps[i].delay_ms));
            const res = await executeStep(steps[i], { skipPauses, batch });
            if (steps[i].type === "run" && res) lastRun = res;
            if (abortRef.current) break;
          }
          if (abortRef.current) {
            // Stopped mid-video: un-strike it so nothing is lost.
            if (targetVideo) yt().unStrikeVideo?.(targetVideo.libId, targetVideo.video_id);
            break;
          }

          // Classify the run outcome for THIS video.
          if (lastRun && lastRun.status === "error") {
            skippedNow.push({ id: `${targetVideo?.video_id || v}-${Date.now()}`, title: lastTitle || "Untitled file", reason: lastRun.error || REASON_LABEL.RUN_FAILED, code: "RUN_FAILED", libId: targetVideo?.libId, video_id: targetVideo?.video_id });
            if (!batch) setGapAlert({ macroName: macro.name, kind: "error", reason: lastRun.error || "the refinery job hit an error before finishing." });
          } else if (lastRun && lastRun.status === "cancelled") {
            if (targetVideo) yt().unStrikeVideo?.(targetVideo.libId, targetVideo.video_id);
            stopMsg = "the refinery job was cancelled";
            if (!batch) setDoneModal({ title: "Refinery run cancelled", message: "The job was cancelled. Whatever finished before you cancelled is in your staging area." });
            break;
          } else if (lastRun) {
            // completed / completed_with_gaps → the job auto-committed to the tier.
            processed++;
            const gaps = lastRun.chunks_skipped || 0;
            if (batch) {
              committed.push({ title: lastTitle, tier, libId: targetVideo?.libId, video_id: targetVideo?.video_id });
              toast.success(`Committed to Tier ${tier} (${committed.length}/${wanted})${gaps ? ` · ${gaps} chunk-gap${gaps === 1 ? "" : "s"}` : ""}.`);
            } else if (lastRun.status === "completed_with_gaps" || gaps > 0) {
              setGapAlert({ macroName: macro.name, kind: "gaps", reason: `${gaps} chunk${gaps === 1 ? "" : "s"} were skipped (the AI service hiccuped and couldn't be recovered after retries). Everything else finished. Look for [SKIPPED] markers in your staging area and re-run just those, or run the whole file again when the service settles.` });
            } else {
              const dest = lastRun.auto_commit_to_tier ? `Tier ${lastRun.auto_commit_to_tier}` : "your staging area";
              setDoneModal({ title: "Refinery run complete", message: `The big file finished cleanly. Results landed in ${dest}.` });
            }
          } else if (batch) {
            // No run step produced a result — nothing to commit.
            skippedNow.push({ id: `${targetVideo?.video_id || v}-${Date.now()}`, title: lastTitle || "Untitled file", reason: REASON_LABEL.EMPTY_OUTPUT, code: "EMPTY_OUTPUT", libId: targetVideo?.libId, video_id: targetVideo?.video_id });
          } else {
            processed++;
            setDoneModal({ title: "Macro complete", message: "Your Refinery macro finished." });
          }
        } catch (stepErr) {
          // Skip-vs-halt classification. The video STAYS STRUCK and lands in the
          // skip report (Re-attempt un-strikes it).
          const code = stepErr?.__yt_code || stepErr?.code || (stepErr?.__refinery_skip ? "REFINERY_TOOBIG" : "UNKNOWN");
          const reason = REASON_LABEL[code] || stepErr?.reason || stepErr?.message || "Skipped";
          skippedNow.push({ id: `${targetVideo?.video_id || v}-${Date.now()}`, title: lastTitle || "Untitled file", reason, code, libId: targetVideo?.libId, video_id: targetVideo?.video_id });
          busRef.current?.clearStaging?.();
          if (!batch) {
            // Single run: surface the reason inline like before.
            if (stepErr?.__refinery_skip) setGapAlert({ macroName: macro.name, kind: "too_big", reason: stepErr.reason || "this file needs Boost or a manual run." });
            else if (stepErr?.__yt_code) setGapAlert({ macroName: macro.name, kind: "error", reason: stepErr.reason || stepErr.message || "a YouTube pull failed." });
            stopMsg = reason;
            break;
          }
          if (HALT_CODES.has(code)) { stopMsg = `stopped: ${reason}`; break; }
          // Otherwise a per-video skip — keep going to the next video.
        }
        if (!usesYouTube) break; // a non-YouTube macro only makes sense once
      }
    } finally {
      const aborted = abortRef.current;
      pausedRef.current = false;
      abortRef.current = false;
      setMode("idle");
      setPlay({ name: "", idx: 0, total: 0, stepLabel: "", editing: false, video: "", batchIdx: 0, batchTotal: 0 });
      if (boostArmed) { try { await busRef.current?.cancelBoostForMacro?.(); } catch { /* ignore */ } }
      // Merge this run's skips into the persistent per-silo report (newest first).
      if (skippedNow.length) persistSkips([...skippedNow, ...skipReport]);

      if (batch) {
        if (notify) fireFinishNotification(macro.name, committed.length, skippedNow.length, tier);
        const skipNote = skippedNow.length ? ` Skipped ${skippedNow.length} (see the skip report — they're left struck).` : "";
        if (aborted) {
          toast.message(`Batch stopped by you — ${committed.length} committed so far.`);
        } else {
          setDoneModal({
            title: stopMsg ? "Batch stopped" : "Batch complete",
            message: `Committed & struck ${committed.length} video${committed.length === 1 ? "" : "s"} to Tier ${tier}.${skipNote}${stopMsg ? ` (${stopMsg})` : ""}`,
          });
        }
      } else if (aborted) {
        toast.message("Macro stopped.");
      } else if (stopMsg && !gapAlert) {
        toast.error(`Macro stopped — ${stopMsg}.`);
      } else if (!processed && skippedNow.length) {
        toast.message(`Skipped — ${skippedNow[0].reason}. It's listed in the skip report and left struck.`);
      }
      // Feb 17, 2026 (Randy pt-run-selected-macro-fix) — ensure struck
      // after a singleUrl run (idempotent). See WorkbenchMacros for the
      // full contract; same behavior here for the Refinery YouTube tool.
      if (singleUrl && processed) {
        try {
          const ytFresh = ytApiRef.current || {};
          await ytFresh.ensureVideoStruckByUrl?.(singleUrl);
        } catch { /* silent — not-in-library = no-op */ }
      }
    }
  };
  // Keep the bus's `runSelectedMacroOnUrl` calling into the LATEST closure.
  runMacroRef.current = runMacro;

  const pausePlay = () => { pausedRef.current = true; setMode("paused"); };
  const resumePlay = () => { pausedRef.current = false; setMode("playing"); };
  const stopPlay = () => { abortRef.current = true; pausedRef.current = false; };

  // ---------- Per-macro batch settings (Save) ----------
  const saveMacroSettings = async (macro) => {
    setSavingSettingsId(macro.id);
    try {
      const body = {
        run_count: Math.max(1, Math.min(50, countByMacro[macro.id] || 1)),
        tier: tierByMacro[macro.id] || 2,
        skip_pauses: skipPausesByMacro[macro.id] !== false,
        notify_on_finish: !!notifyByMacro[macro.id],
      };
      const r = await api.patch(`/workbench/macros/${macro.id}`, body);
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      toast.success("Macro settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings.");
    } finally { setSavingSettingsId(null); }
  };

  // ---------- "Notify me when the batch finishes" (browser notification) ----------
  const toggleNotify = (macroId, checked) => {
    setNotifyByMacro((n) => ({ ...n, [macroId]: checked }));
    if (!checked) return;
    if (!("Notification" in window)) {
      toast.message("This browser can't show notifications. On iPhone, add the silo to your Home Screen first.");
      return;
    }
    if (Notification.permission === "denied") {
      toast.message("Notifications are blocked in your browser settings — turn them on to get a batch-finished ping.");
    } else if (Notification.permission === "default") {
      Notification.requestPermission().then((p) => {
        if (p !== "granted") toast.message("Allow notifications to get pinged when a batch finishes.");
      }).catch(() => {});
    }
  };
  const fireFinishNotification = (macroName, committedN, skippedN, tier) => {
    try {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const body = `Committed ${committedN} to Tier ${tier}${skippedN ? ` · skipped ${skippedN}` : ""}.`;
      const n = new Notification(`Refinery batch finished — ${macroName}`, { body, tag: `refinery-macro-${macroName}` });
      n.onclick = () => { try { window.focus(); n.close(); } catch { /* ignore */ } };
    } catch { /* best-effort */ }
  };

  // ---------- Step editing ----------
  const stepLabelFor = (type, params) => {
    if (type === "set_output_size") return `Set output size → ${params.pct == null ? "Auto" : params.pct + "%"}`;
    if (type === "set_destination") return `When done → ${DEST_LABEL(Number(params.tier) || 0)}`;
    if (type === "run") return `Run "${params.command_name || "operation"}"`;
    return type;
  };
  const openStepEdit = (macro, idx, step) => {
    setEditStep({
      macroId: macro.id, idx, type: step.type,
      pct: step.type === "set_output_size" ? (step.params?.pct ?? null) : null,
      tier: step.type === "set_destination" ? (Number(step.params?.tier) || 0) : 0,
      command_id: step.type === "run" ? (step.params?.command_id || "") : "",
      command_name: step.params?.command_name || "",
      boost: step.type === "run" ? (step.params?.boost || null) : null,
    });
  };
  const saveStepEdit = async (macro, idx, newParams) => {
    const steps = (macro.steps || []).map((s, i) => {
      if (i !== idx) return s;
      const params = { ...s.params, ...newParams };
      return { ...s, params, label: stepLabelFor(s.type, params) };
    });
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      setEditStep(null);
      toast.success("Step updated.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update step."); }
  };

  const deleteMacro = async (macro) => {
    try {
      await api.delete(`/workbench/macros/${macro.id}`);
      setMacros((ms) => ms.filter((m) => m.id !== macro.id));
      toast.success(`Deleted "${macro.name}".`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete macro."); }
  };
  const deleteStep = async (macro, idx) => {
    const nextSteps = (macro.steps || []).filter((_, i) => i !== idx);
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps: nextSteps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update macro."); }
  };
  const moveStep = async (macro, idx, dir) => {
    const steps = [...(macro.steps || [])];
    const j = idx + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[idx], steps[j]] = [steps[j], steps[idx]];
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reorder step."); }
  };
  const saveRename = async (macro) => {
    const name = renameVal.trim();
    if (!name) { setRenameId(null); return; }
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { name });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      setRenameId(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename macro."); }
  };

  const recording = mode === "recording";
  const playingOrPaused = mode === "playing" || mode === "paused";

  return (
    <div className="mt-8" data-testid="refinery-macros">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={18} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-2xl text-[#1C1B1A]">Macros</h3>
        <span className="text-xs text-[#6B6A68]">Record a big-file workflow once, replay it on the next file.</span>
      </div>

      {/* Record controls */}
      <div className="bg-white border hairline rounded-xl p-4 mb-4" data-testid="refinery-macro-record-panel">
        {!recording && !pendingSave && (
          <LockGate locked={macroLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("macro_autobuild")}>
            <button onClick={startRecording} disabled={playingOrPaused} data-testid="refinery-macro-record-start"
              className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-2 disabled:opacity-50">
              <RecordIcon size={16} weight="fill" className="text-[#C16E5A]" /> Record a macro
            </button>
          </LockGate>
        )}

        {recording && (
          <div className="flex flex-wrap items-center gap-3" data-testid="refinery-macro-recording-bar">
            <span className="flex items-center gap-1.5 text-sm text-[#C16E5A] font-medium">
              <Circle size={12} weight="fill" className="animate-pulse" /> Recording · {recordCount} step{recordCount === 1 ? "" : "s"}
            </span>
            <span className="text-xs text-[#6B6A68]">Do your workflow (pick an operation, set the destination, Start refinery). Typing in staging adds a "Pause for editing" step.</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={stopRecording} data-testid="refinery-macro-record-stop"
                className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5">
                <Stop size={14} weight="fill" /> Stop &amp; save
              </button>
              <button onClick={discardRecording} data-testid="refinery-macro-record-discard"
                className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Discard</button>
            </div>
          </div>
        )}

        {pendingSave && (
          <div className="flex flex-wrap items-center gap-2" data-testid="refinery-macro-save-row">
            <span className="text-sm text-[#1C1B1A]">Name this macro:</span>
            <input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveMacro(); }}
              placeholder="e.g. Condense a book → Tier 2"
              data-testid="refinery-macro-save-name"
              className="flex-1 min-w-[220px] bg-[#FDFCF8] border hairline rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <button onClick={saveMacro} disabled={saving} data-testid="refinery-macro-save-confirm"
              className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
              <FloppyDisk size={14} /> Save
            </button>
            <button onClick={discardRecording} data-testid="refinery-macro-save-cancel"
              className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
          </div>
        )}
      </div>

      {/* Gaps / too-big alert (mirrors the Workbench macro's skipped notice) */}
      {gapAlert && (
        <div className="bg-[#FDF1ED] border hairline rounded-xl p-3 mb-4" data-testid="refinery-macro-gap-alert">
          <div className="text-xs text-[#C16E5A] font-medium mb-1 flex items-center gap-1.5">
            <Warning size={14} weight="fill" />
            {gapAlert.kind === "too_big" ? `"${gapAlert.macroName}" — file too big for one job`
              : gapAlert.kind === "error" ? `"${gapAlert.macroName}" — the refinery hit an error`
              : `"${gapAlert.macroName}" — some chunks were skipped`}
          </div>
          <div className="text-[11px] text-[#6B6A68] mb-2">{gapAlert.reason}</div>
          <button onClick={() => setGapAlert(null)} data-testid="refinery-macro-gap-dismiss"
            className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Dismiss</button>
        </div>
      )}

      {/* Skip report — line-by-line, per-silo, persisted. Skipped/errored videos
          stay STRUCK in the library; Re-attempt un-strikes one for a later run,
          Delete drops the row and leaves it struck. */}
      {skipReport.length > 0 && (
        <div className="bg-[#FDF1ED] border hairline rounded-xl p-3 mb-4" data-testid="refinery-macro-skip-report">
          <div className="text-xs text-[#C16E5A] font-medium mb-1 flex items-center gap-1.5">
            <Warning size={14} weight="fill" />
            Skip report · {skipReport.length} video{skipReport.length === 1 ? "" : "s"} left struck
          </div>
          <div className="text-[11px] text-[#6B6A68] mb-2">
            These were skipped (or halted the batch). They stay struck in the library. <span className="font-medium">Re-attempt</span> un-strikes one so a later run picks it up; <span className="font-medium">Delete</span> keeps it struck and just clears the row.
          </div>
          <ul className="space-y-1.5 max-h-56 overflow-y-auto">
            {skipReport.map((it) => (
              <li key={it.id} className="flex items-center gap-2 bg-white border hairline rounded-md px-2.5 py-1.5" data-testid={`refinery-macro-skip-item-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#1C1B1A] truncate" data-testid={`refinery-macro-skip-title-${it.id}`}>{it.title || "Untitled video"}</div>
                  <div className="text-[10px] text-[#C16E5A]" data-testid={`refinery-macro-skip-reason-${it.id}`}>{it.reason}</div>
                </div>
                <button onClick={() => reattemptSkip(it)} data-testid={`refinery-macro-skip-reattempt-${it.id}`}
                  title="Un-strike this video so the next run picks it up"
                  className="text-[11px] px-2 py-1 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#EAF3EC] hover:border-[#4A5D4E] flex items-center gap-1 shrink-0">
                  <ArrowClockwise size={12} /> Re-attempt
                </button>
                <button onClick={() => deleteSkip(it)} data-testid={`refinery-macro-skip-delete-${it.id}`}
                  title="Remove from the report and keep it struck"
                  className="text-[11px] px-2 py-1 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1 shrink-0">
                  <Trash size={12} /> Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Saved macros */}
      {loading ? (
        <div className="text-xs text-[#6B6A68]">Loading macros…</div>
      ) : macros.length === 0 ? (
        <div className="text-xs text-[#6B6A68] italic" data-testid="refinery-macro-empty">
          No macros yet. Hit "Record a macro", run one big file through, and save it.
        </div>
      ) : (
        <div className="space-y-2" data-testid="refinery-macro-list">
          {macros.map((m) => {
            const isOpen = expandedId === m.id;
            const isSelected = m.id === selectedMacroId;
            const handleRowClick = (e) => {
              if (e.target.closest("button, input")) return;
              selectMacro(m);
            };
            return (
              <div
                key={m.id}
                onClick={handleRowClick}
                className={`bg-white border hairline rounded-xl overflow-hidden cursor-pointer transition-colors ${isSelected ? "bg-gradient-to-r from-[#EAF3EC]/40 via-[#EAF3EC]/70 to-[#EAF3EC]/40 ring-1 ring-[#4A5D4E]/30" : "hover:bg-[#FDFCF8]"}`}
                data-testid={`refinery-macro-item-${m.id}`}
                title={isSelected ? "This is your currently-selected Refinery macro (the 'Run selected Macro' button on the YouTube Tool uses it)." : "Click to make this your currently-selected Refinery macro."}
              >
                <div className="flex items-center gap-2 px-4 py-3">
                  <button onClick={() => runMacro(m)} disabled={mode !== "idle"} data-testid={`refinery-macro-run-${m.id}`}
                    className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50 shrink-0">
                    <Play size={14} weight="fill" /> Run
                  </button>
                  {renameId === m.id ? (
                    <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(m); if (e.key === "Escape") setRenameId(null); }}
                      onBlur={() => saveRename(m)} data-testid={`refinery-macro-rename-input-${m.id}`}
                      className="flex-1 bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1C1B1A] font-medium truncate flex items-center gap-1.5">
                        <span className="truncate">{m.name}</span>
                        {macroBoostStep(m) && (
                          <span
                            data-testid={`refinery-macro-boost-badge-${m.id}`}
                            title={macroIsHeavy(m) ? `Boosted with ${boostModelLabel(macroBoostStep(m).params.boost.target_model)} — a premium model. Costs add up.` : `Boosted with ${boostModelLabel(macroBoostStep(m).params.boost.target_model)}.`}
                            className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ${macroIsHeavy(m) ? "bg-[#7A5200] text-white" : "bg-[#EAF3EC] text-[#4A5D4E]"}`}
                          >
                            <Lightning size={9} weight="fill" /> {macroIsHeavy(m) ? "Heavy" : "Boost"}
                          </span>
                        )}
                      </div>
                      <button onClick={() => setExpandedId(isOpen ? null : m.id)} data-testid={`refinery-macro-editsteps-${m.id}`}
                        className="text-[11px] text-[#6B6A68] hover:text-[#4A5D4E] underline decoration-dotted underline-offset-2">
                        {(m.steps || []).length} step{(m.steps || []).length === 1 ? "" : "s"} · {isOpen ? "hide" : "view / edit steps"}
                      </button>
                    </div>
                  )}
                  {/* Feb 17, 2026 (Randy pt-macro-selected) — SELECTED pill. */}
                  {isSelected && (
                    <span
                      data-testid={`refinery-macro-selected-${m.id}`}
                      className="shrink-0 text-[9px] uppercase tracking-[0.28em] font-semibold text-[#2F6B3A] bg-[#EAF3EC] border border-[#B9D4BE] rounded-full px-2 py-0.5"
                      title="This macro is currently SELECTED — the YouTube Tool's Run selected Macro button will use it."
                    >
                      Selected
                    </span>
                  )}
                  <button onClick={() => { setRenameId(m.id); setRenameVal(m.name); }} data-testid={`refinery-macro-rename-${m.id}`}
                    title="Rename" className="p-1.5 text-[#6B6A68] hover:text-[#4A5D4E]"><PencilSimple size={15} /></button>
                  <button onClick={() => setExpandedId(isOpen ? null : m.id)} data-testid={`refinery-macro-expand-${m.id}`}
                    title="View steps" className="p-1.5 text-[#6B6A68] hover:text-[#4A5D4E]">{isOpen ? <CaretDown size={15} /> : <CaretRight size={15} />}</button>
                  <button onClick={() => deleteMacro(m)} data-testid={`refinery-macro-delete-${m.id}`}
                    title="Delete macro" className="p-1.5 text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={15} /></button>
                </div>

                {/* Steps — Iter160t animated grid-template-rows collapse
                    so expanding/collapsing feels smooth. */}
                <div
                  className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="min-h-0">
                {isOpen && (
                  <div className="border-t hairline bg-[#FDFCF8] px-4 py-3" data-testid={`refinery-macro-steps-${m.id}`}>
                    {macroIsHeavy(m) && (
                      <div className="mb-2.5 flex items-start gap-2 text-[11px] bg-[#FFF7E6] border border-[#F0D9A0] rounded-md px-2.5 py-2 text-[#7A5200]" data-testid={`refinery-macro-heavy-warning-${m.id}`}>
                        <Lightning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span>
                          This macro runs on <span className="font-medium">{boostModelLabel(macroBoostStep(m).params.boost.target_model)}</span>, a premium model. On a big file the cost adds up fast. Consider a cheaper "Hitters" model (edit the Run step below) for large jobs.
                        </span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-[#6B6A68]">
                      <span>Run through</span>
                      <input
                        type="number" min={1} max={50}
                        value={countByMacro[m.id] || 1}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1));
                          setCountByMacro((c) => ({ ...c, [m.id]: v }));
                        }}
                        data-testid={`refinery-macro-count-${m.id}`}
                        className="w-16 bg-white border hairline rounded-md px-2 py-1 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                      />
                      <span>video(s)</span>
                      {(countByMacro[m.id] || 1) > 1 && (
                        <>
                          <span>→ auto-commit to</span>
                          <select
                            value={tierByMacro[m.id] || 2}
                            onChange={(e) => setTierByMacro((t) => ({ ...t, [m.id]: parseInt(e.target.value, 10) }))}
                            data-testid={`refinery-macro-tier-${m.id}`}
                            className="bg-white border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                          >
                            <option value={1}>Tier 1</option>
                            <option value={2}>Tier 2</option>
                            <option value={3}>Tier 3</option>
                          </select>
                          <label className="flex items-center gap-1.5 cursor-pointer ml-1" data-testid={`refinery-macro-skip-pauses-label-${m.id}`}>
                            <input
                              type="checkbox"
                              checked={skipPausesByMacro[m.id] !== false}
                              onChange={(e) => setSkipPausesByMacro((sp) => ({ ...sp, [m.id]: e.target.checked }))}
                              data-testid={`refinery-macro-skip-pauses-${m.id}`}
                            />
                            <span>Skip “Pause for editing” steps</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer ml-1" data-testid={`refinery-macro-notify-label-${m.id}`}>
                            <input
                              type="checkbox"
                              checked={!!notifyByMacro[m.id]}
                              onChange={(e) => toggleNotify(m.id, e.target.checked)}
                              data-testid={`refinery-macro-notify-${m.id}`}
                            />
                            <Bell size={12} weight="fill" className="text-[#4A5D4E]" />
                            <span>Notify me when it finishes</span>
                          </label>
                        </>
                      )}
                      <button
                        onClick={() => saveMacroSettings(m)}
                        disabled={savingSettingsId === m.id}
                        data-testid={`refinery-macro-save-settings-${m.id}`}
                        className="ml-auto text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                      >
                        <FloppyDisk size={12} /> {savingSettingsId === m.id ? "Saving…" : "Save"}
                      </button>
                    </div>
                    <div className="text-[11px] text-[#6B6A68] mb-2 italic">
                      {(countByMacro[m.id] || 1) > 1
                        ? "Batch: walks your library from the bottom up. For each video it pulls the transcript, runs your steps, auto-commits the result to the tier above, strikes the video, and moves on. Pause steps are skipped when the box above is ticked. A video with no transcript / unavailable / blank / too-big-for-one-job is skipped (left struck) and listed in the skip report — the batch keeps going. It only STOPS on a systemic problem (quota, bad key, empty voucher). Remember to Save if you change the count/tier/pauses."
                        : "Runs your recorded steps once on the next un-struck video (bottom of the library, upward) — or the file in the slot. The Run step waits for the whole background job to finish. Reorder with ↑↓, edit choice-steps with ✎, remove with ✕. Remember to Save if you change the count/tier/pauses."}
                    </div>
                    <ol className="space-y-1.5">
                      {(m.steps || []).map((s, i) => {
                        const editable = s.type === "set_output_size" || s.type === "run" || s.type === "set_destination";
                        const editing = editStep && editStep.macroId === m.id && editStep.idx === i;
                        return (
                          <li key={i} className="text-xs" data-testid={`refinery-macro-step-${m.id}-${i}`}>
                            <div className="flex items-center gap-1.5">
                              <span className="w-5 text-right text-[#9A9890] tabular-nums">{i + 1}.</span>
                              <span className={`flex-1 ${s.type === "pause_for_editing" ? "text-[#C16E5A]" : "text-[#1C1B1A]"}`}>
                                {s.type === "pause_for_editing" && <Pause size={11} weight="fill" className="inline mr-1" />}
                                {s.label || s.type}
                              </span>
                              {editable && (
                                <button onClick={() => (editing ? setEditStep(null) : openStepEdit(m, i, s))} data-testid={`refinery-macro-step-edit-${m.id}-${i}`}
                                  title="Edit this step's choice" className="p-1 text-[#9A9890] hover:text-[#4A5D4E]"><PencilSimple size={12} /></button>
                              )}
                              <button onClick={() => moveStep(m, i, -1)} disabled={i === 0} data-testid={`refinery-macro-step-up-${m.id}-${i}`}
                                title="Move up" className="p-1 text-[#9A9890] hover:text-[#4A5D4E] disabled:opacity-30"><ArrowUp size={12} /></button>
                              <button onClick={() => moveStep(m, i, 1)} disabled={i === (m.steps || []).length - 1} data-testid={`refinery-macro-step-down-${m.id}-${i}`}
                                title="Move down" className="p-1 text-[#9A9890] hover:text-[#4A5D4E] disabled:opacity-30"><ArrowDown size={12} /></button>
                              <button onClick={() => deleteStep(m, i)} data-testid={`refinery-macro-step-delete-${m.id}-${i}`}
                                title="Delete this step" className="p-1 text-[#9A9890] hover:text-[#C16E5A]"><X size={12} /></button>
                            </div>
                            {editing && (
                              <div className="mt-1.5 ml-6 p-2.5 bg-white border hairline rounded-md flex flex-wrap items-center gap-2" data-testid={`refinery-macro-step-editor-${m.id}-${i}`}>
                                {editStep.type === "set_output_size" ? (
                                  <>
                                    <label className="flex items-center gap-1 text-xs text-[#6B6A68]">
                                      <input type="checkbox" checked={editStep.pct == null}
                                        onChange={(e) => setEditStep((es) => ({ ...es, pct: e.target.checked ? null : 50 }))}
                                        data-testid={`refinery-macro-step-edit-auto-${m.id}-${i}`} /> Auto
                                    </label>
                                    {editStep.pct != null && (
                                      <>
                                        <input type="range" min={10} max={100} step={5} value={editStep.pct}
                                          onChange={(e) => setEditStep((es) => ({ ...es, pct: parseInt(e.target.value, 10) }))}
                                          data-testid={`refinery-macro-step-edit-slider-${m.id}-${i}`} className="flex-1 min-w-[120px]" />
                                        <span className="text-xs tabular-nums w-10 text-right">{editStep.pct}%</span>
                                      </>
                                    )}
                                    <button onClick={() => saveStepEdit(m, i, { pct: editStep.pct })} data-testid={`refinery-macro-step-edit-save-${m.id}-${i}`}
                                      className="text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">Save</button>
                                    <button onClick={() => setEditStep(null)} className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
                                  </>
                                ) : editStep.type === "set_destination" ? (
                                  <>
                                    <span className="text-xs text-[#6B6A68]">When done:</span>
                                    <select value={editStep.tier}
                                      onChange={(e) => setEditStep((es) => ({ ...es, tier: parseInt(e.target.value, 10) }))}
                                      data-testid={`refinery-macro-step-edit-dest-${m.id}-${i}`}
                                      className="bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                                      <option value={0}>Stage only</option>
                                      <option value={1}>Commit to T1 (Anchor)</option>
                                      <option value={2}>Commit to T2 (Logic)</option>
                                      <option value={3}>Commit to T3 (Library)</option>
                                    </select>
                                    <button onClick={() => saveStepEdit(m, i, { tier: editStep.tier })} data-testid={`refinery-macro-step-edit-save-${m.id}-${i}`}
                                      className="text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">Save</button>
                                    <button onClick={() => setEditStep(null)} className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-xs text-[#6B6A68]">Operation:</span>
                                    <select value={editStep.command_id || ""}
                                      onChange={(e) => {
                                        const id = e.target.value;
                                        const ops = busRef.current?.getCommands?.() || [];
                                        const op = ops.find((o) => o.id === id);
                                        setEditStep((es) => ({ ...es, command_id: id, command_name: op?.name || "" }));
                                      }}
                                      data-testid={`refinery-macro-step-edit-op-${m.id}-${i}`}
                                      className="flex-1 min-w-[160px] bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                                      {(busRef.current?.getCommands?.() || []).map((o) => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                      ))}
                                    </select>
                                    {/* Boost model for this Run step (Randy Jul 2026): change the LLM the
                                        macro replays on. "Heavy Hitters" are premium/expensive. */}
                                    <div className="w-full flex flex-wrap items-center gap-2 mt-1.5 pt-1.5 border-t border-[#E0DED8]">
                                      <span className="text-xs text-[#6B6A68] flex items-center gap-1"><Lightning size={12} weight="fill" className="text-[#7A5200]" /> Boost:</span>
                                      <select
                                        value={editStep.boost?.target_model || ""}
                                        onChange={(e) => {
                                          const id = e.target.value;
                                          if (!id) { setEditStep((es) => ({ ...es, boost: null })); return; }
                                          const meta = boostModelMeta(id);
                                          setEditStep((es) => ({ ...es, boost: {
                                            target_model: id,
                                            target_provider: meta?.provider || null,
                                            target_max_tokens: meta?.max_output_tokens || null,
                                            desired_output_chars: es.boost?.desired_output_chars ?? null,
                                          } }));
                                        }}
                                        data-testid={`refinery-macro-step-edit-boost-model-${m.id}-${i}`}
                                        className="flex-1 min-w-[160px] bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                                      >
                                        <option value="">No boost — default model</option>
                                        <optgroup label="Hitters (cheaper)">
                                          {SMALL_BOOST_MODELS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </optgroup>
                                        <optgroup label="Heavy Hitters (premium)">
                                          {HEAVY_BOOST_MODELS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </optgroup>
                                      </select>
                                      {editStep.boost?.target_model && isHeavyBoostModel(editStep.boost.target_model) && (
                                        <span className="text-[10px] text-[#7A5200] flex items-center gap-0.5" data-testid={`refinery-macro-step-edit-boost-premium-${m.id}-${i}`}><Warning size={10} weight="fill" /> premium — costs add up</span>
                                      )}
                                    </div>
                                    <button onClick={() => saveStepEdit(m, i, { command_id: editStep.command_id, command_name: editStep.command_name, boost: editStep.boost || null })} data-testid={`refinery-macro-step-edit-save-${m.id}-${i}`}
                                      className="text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">Save</button>
                                    <button onClick={() => setEditStep(null)} className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
                                  </>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating shelf while playing */}
      {playingOrPaused && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[120] w-[92vw] max-w-lg bg-[#1C1B1A] text-[#FDFCF8] rounded-xl shadow-2xl px-4 py-3 flex flex-col gap-2" data-testid="refinery-macro-shelf">
          {!play.editing && play.idx > 0 && (
            <div className="w-full flex items-center gap-2 text-[11px] text-[#E8D9B0] border-b border-white/10 pb-1.5" data-testid="refinery-macro-shelf-step">
              <span className="shrink-0 tabular-nums px-1.5 py-0.5 rounded bg-white/10">Step {play.idx}/{play.total}</span>
              <span className="truncate font-medium">{play.stepLabel || "…"}</span>
            </div>
          )}
          <div className="w-full flex items-center gap-3">
            <span className="flex items-center gap-2 min-w-0">
              {mode === "paused"
                ? <Pause size={18} weight="fill" className="text-[#E8B04B] shrink-0" />
                : <Play size={18} weight="fill" className="text-[#8FB08C] shrink-0 animate-pulse" />}
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{play.name || "Macro"}</span>
                <span className="block text-[11px] text-[#B9B7B0]">
                  {play.editing ? "Editing paused — make your changes, then Resume"
                    : play.stepLabel && /^Run /.test(play.stepLabel) ? "Refining in the background — this can take a while…"
                    : `Step ${play.idx} of ${play.total}`}
                </span>
              </span>
            </span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {mode === "paused" ? (
                <button onClick={resumePlay} data-testid="refinery-macro-shelf-resume"
                  className="text-sm px-3 py-1.5 rounded-md bg-[#8FB08C] text-[#1C1B1A] hover:bg-[#7CA079] flex items-center gap-1.5 font-medium">
                  <Play size={14} weight="fill" /> Resume
                </button>
              ) : (
                <button onClick={pausePlay} data-testid="refinery-macro-shelf-pause"
                  className="text-sm px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 flex items-center gap-1.5">
                  <Pause size={14} weight="fill" /> Pause
                </button>
              )}
              <button onClick={stopPlay} data-testid="refinery-macro-shelf-stop"
                className="text-sm px-3 py-1.5 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85A48] flex items-center gap-1.5">
                <Stop size={14} weight="fill" /> Stop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Heavy-hitter cost gate — premium-model warning before a run. */}
      {heavyConfirm && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 px-4" data-testid="refinery-macro-heavy-confirm">
          <div className="modal-safe bg-[#FDFCF8] rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-full bg-[#FFF7E6] text-[#7A5200] flex items-center justify-center mx-auto mb-3">
              <Lightning size={24} weight="fill" />
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mb-1.5 text-center">Premium model — heads up</h3>
            <p className="text-sm text-[#6B6A68] mb-4 leading-relaxed text-center">
              “{heavyConfirm.macro.name}” runs on <span className="font-medium text-[#1C1B1A]">{heavyConfirm.model}</span>, a premium model. On a big file you're billed for every chunk.
              <span className="block mt-2 text-[#7A5200]">Rough estimate: <span className="font-medium" data-testid="refinery-macro-heavy-estimate">~${(heavyConfirm.estCents / 100).toFixed(2)}</span> (varies with file size). A cheaper "Hitters" model can do the same for a fraction — edit the Run step to switch.</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setHeavyConfirm(null)} data-testid="refinery-macro-heavy-cancel"
                className="flex-1 py-2.5 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] text-sm">Cancel</button>
              <button onClick={() => { const mac = heavyConfirm.macro; setHeavyConfirm(null); runMacro(mac, { heavyConfirmed: true }); }} data-testid="refinery-macro-heavy-proceed"
                className="flex-1 py-2.5 rounded-md bg-[#7A5200] text-white hover:bg-[#5E3F00] text-sm font-medium">Run anyway</button>
            </div>
          </div>
        </div>
      )}

      {/* Done modal */}
      {doneModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 px-4" data-testid="refinery-macro-done-modal">
          <div className="modal-safe bg-[#FDFCF8] rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#EAF3EC] text-[#2F8F4E] flex items-center justify-center mx-auto mb-3">
              <Check size={24} weight="bold" />
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mb-1.5">{doneModal.title}</h3>
            <p className="text-sm text-[#6B6A68] mb-5 leading-relaxed">{doneModal.message}</p>
            <button onClick={() => setDoneModal(null)} data-testid="refinery-macro-done-ok"
              className="w-full py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm font-medium">OK</button>
          </div>
        </div>
      )}
    </div>
  );
}
